import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Building2, LogIn, UserCog } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { authApi, getUserInfo, unidadesApi } from '../api/backend';
import { ThemeToggle } from '../components/ThemeToggle';

export function LoginPage() {
  const navigate = useNavigate();
  const { user, setUser, setLoading, setSelectedUnidade, setImpersonating } = useAuthStore();
  
  // Estado para login de unidade
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [showAdminChoice, setShowAdminChoice] = useState(false);
  const [unidades, setUnidades] = useState<any[]>([]);
  const [loadingUnidades, setLoadingUnidades] = useState(false);
  const [adminUserInfo, setAdminUserInfo] = useState<any>(null);

  useEffect(() => {
    // Check if user is already logged in
    const checkAuth = async () => {
      try {
        const userInfo = await getUserInfo();
        if (userInfo.display_name) {
          // Já está logado como admin
          setAdminUserInfo(userInfo);
          setShowAdminChoice(true);
          setLoading(false);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Auth check error:', error);
        setLoading(false);
      }
    };

    checkAuth();
  }, [setLoading]);

  const loadUnidades = async () => {
    try {
      setLoadingUnidades(true);
      const data = await unidadesApi.list();
      setUnidades(data);
    } catch (error) {
      console.error('Error loading unidades:', error);
      alert('Erro ao carregar unidades');
    } finally {
      setLoadingUnidades(false);
    }
  };

  const handleAdminLogin = async () => {
    try {
      const userInfo = await getUserInfo();
      if (userInfo.display_name) {
        setAdminUserInfo(userInfo);
        setShowAdminChoice(true);
        await loadUnidades();
      } else {
        alert('Por favor, faça login no Youware primeiro.');
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Erro ao fazer login. Tente novamente.');
    }
  };

  const handleAdminAsAdmin = () => {
    if (adminUserInfo) {
      setUser(adminUserInfo, 'admin');
      setImpersonating(false);
      setSelectedUnidade(null);
      navigate('/dashboard');
    }
  };

  const handleAdminAsUnidade = (unidadeId: number) => {
    if (adminUserInfo) {
      setUser(adminUserInfo, 'admin');
      setImpersonating(true);
      setSelectedUnidade(unidadeId);
      navigate('/dashboard');
    }
  };

  const handleUnidadeLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setLoggingIn(true);

    try {
      const data = await authApi.clienteLogin(username, password);

      if (data.success) {
        // Login bem-sucedido
        setUser({
          display_name: data.nome,
          //encrypted_yw_id: `unidade_${data.unidade_id}`,
          //photo_url: data.logo_url,
          //unidade_id: data.unidade_id,
          //username: data.username,
          nome: data.nome,
          //codigo: data.codigo,
          //cpf: data.cpf,
          //email: data.email,
          //telefone: data.telefone,
          //cargo: data.cargo,
        } as any, 'unidade');
        setImpersonating(false);
        setSelectedUnidade(null);
        navigate('/dashboard');
      } else {
        setLoginError(data?.error || 'Credenciais inválidas');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Erro ao conectar ao servidor');
    } finally {
      setLoggingIn(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>

      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl mb-4 shadow-lg">
            <FileText className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            AXIS PC REPORT
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Sistema de Prestação de Contas
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 border border-gray-200 dark:border-gray-700">
          {showAdminChoice ? (
            <>
              {/* Admin Choice: Login como Admin ou como Unidade */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-6">
                  <UserCog className="w-6 h-6 text-primary-600" />
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    Bem-vindo, {adminUserInfo?.display_name}!
                  </h2>
                </div>

                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Escolha como deseja acessar o sistema:
                </p>

                {/* Opção 1: Admin Normal */}
                <button
                  onClick={handleAdminAsAdmin}
                  className="w-full mb-4 p-4 border-2 border-primary-500 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                      <UserCog className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="font-semibold text-gray-900 dark:text-white">
                        Entrar como Administrador
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Acesso completo ao sistema com todas as permissões
                      </div>
                    </div>
                  </div>
                </button>

                {/* Opção 2: Visualizar como Unidade */}
                {/*
                <div className="mb-4">
                  <div className="font-medium text-gray-900 dark:text-white mb-3">
                    Ou visualizar como uma unidade:
                  </div>
                  
                  {loadingUnidades ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                        Carregando unidades...
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {unidades.map((unidade) => (
                        <button
                          key={unidade.id}
                          onClick={() => handleAdminAsUnidade(unidade.id)}
                          className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all text-left group"
                        >
                          <div className="flex items-center gap-3">
                            <Building2 className="w-5 h-5 text-gray-400 group-hover:text-primary-600 transition-colors" />
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">
                                {unidade.nome}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                Código: {unidade.codigo}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                */}

                <button
                  onClick={() => {
                    setShowAdminChoice(false);
                    setAdminUserInfo(null);
                  }}
                  className="w-full bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white py-3 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium"
                >
                  Cancelar
                </button>
              </div>
            </>
          ) : !showAdminLogin ? (
            <>
              {/* Unidade Login Form */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-6">
                  <Building2 className="w-6 h-6 text-primary-600" />
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    Login de Unidade
                  </h2>
                </div>

                <form onSubmit={handleUnidadeLogin} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Usuário
                    </label>
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                      placeholder="Digite seu usuário"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Senha
                    </label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                      placeholder="Digite sua senha"
                      required
                    />
                  </div>

                  {loginError && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                      <p className="text-sm text-red-800 dark:text-red-200">{loginError}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loggingIn}
                    className="w-full bg-primary-600 text-white py-3 rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loggingIn ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>Entrando...</span>
                      </>
                    ) : (
                      <>
                        <LogIn className="w-5 h-5" />
                        <span>Entrar</span>
                      </>
                    )}
                  </button>
                </form>
              </div>

              {/*
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white dark:bg-gray-800 text-gray-500">ou</span>
                </div>
              </div>
            
              <button
                onClick={handleAdminLogin}
                className="w-full bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white py-3 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium"
              >
                Entrar como Administrador
              </button>
              */}
            </>
          ) : (
            <>
              {/* Admin Login (caso não tenha getUserInfo) */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-6">
                  <LogIn className="w-6 h-6 text-primary-600" />
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    Login Administrativo
                  </h2>
                </div>

                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Acesso via autenticação Youware para administradores do sistema.
                </p>

                <button
                  onClick={handleAdminLogin}
                  className="w-full bg-primary-600 text-white py-3 rounded-lg hover:bg-primary-700 transition-colors font-medium flex items-center justify-center gap-2"
                >
                  <LogIn className="w-5 h-5" />
                  <span>Autenticar com Youware</span>
                </button>
              </div>

              <button
                onClick={() => setShowAdminLogin(false)}
                className="w-full bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white py-3 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium"
              >
                Voltar para Login de Unidade
              </button>
            </>
          )}
        </div>

        {/* Footer Info */}
        <div className="mt-8 text-center text-sm text-gray-600 dark:text-gray-400">
          <p>Sistema de Gestão de Prestação de Contas</p>
          <p className="mt-1">AXIS Tecnologia - {new Date().getFullYear()}</p>
        </div>
      </div>
    </div>
  );
}
